import type { QueryClient } from '@tanstack/react-query';

import type { EntityCounts, SoftDeleteEntityType } from '@/common/soft-delete';
import { t } from '@/i18n';

/** Display order: parents first, as the backend cascades. */
const ORDER: SoftDeleteEntityType[] = [
	'ASSET',
	'SUB_ASSET',
	'INSPECTION',
	'DAMAGE',
	'POLYGON',
	'DATA_UPLOAD',
	'WORK_PACKAGE',
	'WORK_PACKAGE_ITEM',
];

const ENTITY_LABEL = {
	ASSET: 'common.entities.asset',
	SUB_ASSET: 'common.entities.subAsset',
	INSPECTION: 'common.entities.inspection',
	DAMAGE: 'common.entities.damage',
	POLYGON: 'common.entities.polygon',
	DATA_UPLOAD: 'common.entities.file',
	WORK_PACKAGE: 'common.entities.workPackage',
	WORK_PACKAGE_ITEM: 'common.entities.workPackageItem',
} as const satisfies Record<SoftDeleteEntityType, string>;

/**
 * "4 inspections, 37 damages" for what a delete or restore takes along, without the item itself
 * (one row of `rootType`). Empty when nothing else is affected.
 */
export function describeAlsoAffected(counts: EntityCounts, rootType: SoftDeleteEntityType): string {
	return describeCounts({ ...counts, [rootType]: (counts[rootType] ?? 0) - 1 });
}

/** "1 damage, 2 files" for per-entity counts; empty when all are zero. */
export function describeCounts(counts: EntityCounts): string {
	return ORDER.map((type) => {
		const count = counts[type] ?? 0;
		return count > 0 ? `${count} ${t(ENTITY_LABEL[type], { count })}` : null;
	})
		.filter(Boolean)
		.join(', ');
}

/**
 * Query keys a delete or restore can change: it cascades across entities, so lists of every kind
 * (and counts on asset cards) may change. Prefix keys, so detail and list queries are covered.
 */
const AFFECTED_QUERY_KEYS = [
	['assets'],
	['damages'],
	['damage'],
	['damage-polygons'],
	['data-uploads'],
	['work-packages'],
] as const;

/** Whether a query key names one of `ids` anywhere, e.g. ['assets', 'a1', 'inspections', { inspectionId: 'i1' }]. */
function keyMentions(value: unknown, ids: ReadonlySet<string>): boolean {
	if (typeof value === 'string') {
		return ids.has(value);
	}
	if (Array.isArray(value)) {
		return value.some((item) => keyMentions(item, ids));
	}
	if (value && typeof value === 'object') {
		return Object.values(value).some((item) => keyMentions(item, ids));
	}
	return false;
}

/**
 * Refreshes what a delete or restore of `ids` changed. Queries about the items themselves (their detail,
 * live or deleted, and everything keyed by them) are only marked stale: right after a delete their live
 * endpoints answer 404, and right after a restore their deleted ones do, while the page that shows them
 * is still open. They load again on the next page that needs them.
 */
export async function invalidateAfterSoftDelete(queryClient: QueryClient, ids: string[] = []) {
	const changed = new Set(ids);
	const isAboutChanged = (queryKey: readonly unknown[]) => keyMentions(queryKey, changed);
	await Promise.all(
		AFFECTED_QUERY_KEYS.flatMap((queryKey) => [
			queryClient.invalidateQueries({ queryKey: [...queryKey], predicate: (query) => !isAboutChanged(query.queryKey) }),
			queryClient.invalidateQueries({
				queryKey: [...queryKey],
				predicate: (query) => isAboutChanged(query.queryKey),
				refetchType: 'none',
			}),
		]),
	);
}
